import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

interface PortalProps {
  position: [number, number, number]
  color: string
  variant: 'start' | 'home'
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
}

const VORTEX_FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform vec3 uColor;
  void main() {
    vec2 c = vUv - 0.5;
    float r = length(c);
    float a = atan(c.y, c.x);
    float swirl = sin(a * 5.0 + uTime * 3.0 - r * 18.0);
    float ring = smoothstep(0.5, 0.15, r);
    float glow = smoothstep(0.5, 0.0, r);
    float alpha = ring * (0.4 + 0.6 * (swirl * 0.5 + 0.5));
    vec3 col = uColor * (0.6 + glow * 1.4);
    gl_FragColor = vec4(col, alpha * ring);
    if (gl_FragColor.a < 0.02) discard;
  }
`
const VORTEX_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const BEACON_FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform vec3 uColor;
  void main() {
    float fade = pow(1.0 - vUv.y, 1.6);
    float flicker = 0.85 + 0.15 * sin(uTime * 2.2 + vUv.y * 6.0);
    gl_FragColor = vec4(uColor, fade * 0.32 * flicker);
  }
`

export function Portal({ position, color, variant, reducedMotion, graphics }: PortalProps) {
  const ref = useRef<THREE.Mesh>(null)
  const uniforms = useMemo(
    () => ({ uTime: { value: 0 }, uColor: { value: new THREE.Color(color) } }),
    [color],
  )
  useFrame((_, delta) => {
    if (graphics === 'low') return
    if (reducedMotion) return
    uniforms.uTime.value += delta
    if (ref.current) {
      ref.current.rotation.z += delta * (variant === 'home' ? 0.8 : -0.8)
      if (variant === 'home') {
        const pulse = 1 + Math.sin(uniforms.uTime.value * 2) * 0.06
        ref.current.scale.setScalar(pulse)
      }
    }
  })

  const size = variant === 'home' ? 1.5 : 1.25

  if (graphics === 'low') {
    return (
      <group position={position}>
        <mesh position={[0, 0.268, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[size * 0.34, 24]} />
          <meshBasicMaterial color={color} transparent opacity={variant === 'home' ? 0.58 : 0.42} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.272, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[size * 0.38, size * 0.48, 24]} />
          <meshBasicMaterial color={color} transparent opacity={0.46} toneMapped={false} />
        </mesh>
        {variant === 'home' && (
          <mesh position={[0, 0.78, 0]}>
            <coneGeometry args={[0.26, 0.9, 18, 1, true]} />
            <meshBasicMaterial color={color} transparent opacity={0.22} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        )}
      </group>
    )
  }

  return (
    <group position={position}>
      <mesh ref={ref} position={[0, 0.27, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[size, size]} />
        <shaderMaterial
          uniforms={uniforms}
          vertexShader={VORTEX_VERT}
          fragmentShader={VORTEX_FRAG}
          transparent
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {variant === 'home' && (
        <mesh position={[0, 1.35, 0]}>
          <cylinderGeometry args={[0.3, 0.42, 2.2, 20, 1, true]} />
          <shaderMaterial
            uniforms={uniforms}
            vertexShader={VORTEX_VERT}
            fragmentShader={BEACON_FRAG}
            transparent
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      <pointLight color={color} distance={variant === 'home' ? 4.5 : 3} intensity={variant === 'home' ? 3 : 1.4} position={[0, 0.7, 0]} />
    </group>
  )
}
